import Icon, { ICON_NAMES } from "shared/components/utils/Icon";
import {
    StyledTd,
    StyledText,
} from "./BodyCell.styles";
import Text, { VARIANTS } from "shared/components/data-display/text/Text";

import { COLOR_VALUES } from "shared/config/colors";
import Dotdotdot from "react-dotdotdot";
import HStack from "shared/components/layout/stack/HStack";
import { MenuItem } from "@szhsin/react-menu";
import MenuItemButton from "shared/components/inputs/button/MenuItemButton";
import MoreMenu from "shared/components/inputs/menu/MoreMenu";
import PropTypes from "prop-types";
import TertiaryButton from "shared/components/inputs/button/TertiaryButton";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import { makeStyles } from "@material-ui/core/styles";
import { moreMenuClassStyle } from "shared/components/data-display/tables/MoreMenuStyle.styles";

const useStyles = makeStyles(() => ({
    moreMenu: moreMenuClassStyle,
}));

const BodyCell = ({
    text,
    description,
    icon = null,
    iconWidth,
    iconHeight,
    iconViewBox,
    iconColor,
    justifyContent = "start",
    actionIcon,
    actionProps = {},
    tooltip,
    placement,
    tooltipEnterDelay = 2000,
    compact = false,
    shownByRowHover = false,
    color,
    padding,
    borderColor,
    variant = VARIANTS.Body_2_1,
    moreMenuActionProps = [],
    ellipsisAtLine = null,
    expanded,
    actionIconWidth = 16,
    cellTextOnMouseEnter = () => void 0,
    cellTextOnTextMouseLeave = () => void 0,
    ...rest
}) => {
    const hasCellText = text || description;
    const hasAction =
        typeof actionProps === "object" &&
        ["to", "href", "onClick"].some((prop) =>
            Object.keys(actionProps).includes(prop)
        );
    const hasMoreMenu = moreMenuActionProps.length > 0;
    const classes = useStyles();

    return (
        <StyledTd
            $compact={compact}
            $shownByRowHover={shownByRowHover}
            padding={padding}
            borderColor={borderColor}
            {...rest}
        >
            <>
                <Tooltip
                    text={tooltip}
                    enabled={!!tooltip}
                    placement={placement}
                    enterDelay={tooltipEnterDelay}
                >
                    <HStack
                        align="center"
                        justify={hasCellText ? justifyContent : "center"}
                        spacing="Zero_5"
                    >
                        {icon && (
                            <Icon
                                name={icon}
                                color={iconColor}
                                width={iconWidth}
                                height={iconHeight}
                                viewBox={iconViewBox}
                            />
                        )}
                        {hasMoreMenu && (
                            <MoreMenu
                                alwaysVisible={true}
                                position="initial"
                                moreMenuClass={classes.moreMenu}
                            >
                                {moreMenuActionProps.map(
                                    (menuItemData) => (
                                        <MenuItem
                                            key={menuItemData.text}
                                            disabled={false}
                                        >
                                            <MenuItemButton
                                                {...menuItemData.actionProps}
                                                label={menuItemData.text}
                                                trailingIcon={
                                                    menuItemData.actionIcon
                                                }
                                                style={{
                                                    color: "currentColor",
                                                    ...(hasCellText && {
                                                        marginLeft: "-8px",
                                                        marginTop: "-4px",
                                                        marginBottom: "-4px",
                                                    }),
                                                }}
                                            />
                                        </MenuItem>
                                    )
                                )}
                            </MoreMenu>
                        )}
                        {(hasCellText || hasAction) && (
                            <>
                                {hasAction ? (
                                    <TertiaryButton
                                        {...actionProps}
                                        trailingIcon={actionIcon}
                                        iconWidth={actionIconWidth}
                                        label={text}
                                        style={{
                                            color: "currentColor",
                                            ...(hasCellText && {
                                                marginLeft: "-8px",
                                                marginTop: "-4px",
                                                marginBottom: "-4px",
                                                wordBreak: "break-all",
                                                background: "transparent",
                                            }),
                                        }}
                                    />
                                ) : (
                                    text && (
                                        <StyledText
                                            expanded={expanded}
                                            ellipsisAtLine={ellipsisAtLine}
                                            color={color}
                                            variant={variant}
                                            onMouseEnter={cellTextOnMouseEnter}
                                            onMouseLeave={cellTextOnTextMouseLeave}
                                        >
                                            {!ellipsisAtLine || expanded
                                                ? text
                                                : !expanded && (
                                                    <Dotdotdot clamp={ellipsisAtLine}>
                                                        {text}
                                                    </Dotdotdot>
                                                )}
                                        </StyledText>
                                    )
                                )}
                            </>
                        )}
                    </HStack>
                </Tooltip>
                {description && (
                    <Text variant="Descriptive_2_1" color="Gray_2">
                        {description}
                    </Text>
                )}
            </>
        </StyledTd>
    );
};

BodyCell.propTypes = {
    text: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
    ]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    icon: PropTypes.oneOf(ICON_NAMES),
    iconWidth: PropTypes.number,
    iconHeight: PropTypes.number,
    iconViewBox: PropTypes.string,
    iconColor: PropTypes.oneOf(Object.keys(COLOR_VALUES)),
    justifyContent: PropTypes.string,
    actionIcon: PropTypes.oneOfType([
        PropTypes.oneOf(ICON_NAMES),
        PropTypes.bool,
    ]),
    actionProps: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    tooltip: PropTypes.string,
    placement: PropTypes.string,
    tooltipEnterDelay: PropTypes.number,
    compact: PropTypes.bool,
    shownByRowHover: PropTypes.bool,
    color: PropTypes.string,
    padding: PropTypes.string,
    borderColor: PropTypes.string,
    variant: PropTypes.oneOf(Object.values(VARIANTS)),
    moreMenuActionProps: PropTypes.array,
    ellipsisAtLine: PropTypes.number,
    expanded: PropTypes.bool,
    actionIconWidth: PropTypes.number,
    cellTextOnMouseEnter: PropTypes.func,
    cellTextOnTextMouseLeave: PropTypes.func,
};

export default BodyCell;

